import { useState, useEffect, useRef, createContext, useContext } from 'react'
import { createPortal } from 'react-dom'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import {
  LayoutDashboard, Users, Building2, Gavel, Package2, FileText,
  CreditCard, Menu, X, LogOut, BarChart3, ChevronDown, ChevronRight,
  Receipt, Cog, UserCog, Banknote, Calendar, Beef,
  Bell, Settings, PanelLeftClose, PanelLeftOpen, AlertTriangle, ArrowLeftFromLine,
  Map, Truck, TrendingUp, DollarSign, Tag, Wallet, Globe, Brain, Send, EyeOff, Key, Coins, Link2, MessageCircle, History, ShieldOff
} from 'lucide-react'
import { useAuthStore } from '../../stores/authStore'
import api from '../../services/api'
import { useOrganizador } from '../../contexts/OrganizadorContext'
import organizadoresService from '../../services/organizadoresService'

// ─── Contexto sidebar (mantido para compatibilidade com páginas existentes) ───
const SidebarContext = createContext()
export const useSidebar = () => {
  const ctx = useContext(SidebarContext)
  if (!ctx) throw new Error('useSidebar deve ser usado dentro do Layout')
  return ctx
}

// ─── Mapa de títulos por rota ────────────────────────────────────────────────
const ROUTE_TITLES = {
  '/dashboard': 'Dashboard',
  '/clientes': 'Clientes',
  '/leiloes': 'Leilões',
  '/lotes': 'Lotes',
  '/ordem-entrada': 'Ordem de Entrada',
  '/ordem-entrada/filtros-automaticos': 'Filtros Automáticos',
  '/ordem-entrada/filtros-automaticos/novo': 'Novo Filtro',
  '/ordem-entrada/justificativas': 'Justificativas',
  '/catalogos': 'Catálogos',
  '/catalogos/leilao': 'Catálogo Leilão',
  '/catalogos/pisteiro': 'Catálogo Pisteiro',
  '/catalogos/detalhado': 'Catálogo Detalhado',
  '/catalogos/ficha-leiloeiro': 'Ficha do Leiloeiro',
  '/catalogos/ficha-curral': 'Ficha do Curral',
  '/catalogos/transmissao': 'Cat. Transmissão',
  '/mapas': 'Mapas',
  '/mapas/comprador': 'Mapa do Comprador',
  '/mapas/fatura-comprador': 'Fatura do Comprador',
  '/mapas/vendedor': 'Mapa do Vendedor',
  '/mapas/fatura-vendedor': 'Fatura do Vendedor',
  '/mapas/fatura-acerto': 'Fatura Acerto',
  '/relatorios/lista-vendedores': 'Lista de Vendedores',
  '/portaria': 'Portaria',
  '/portaria/entrada': 'Entrada de Animais',
  '/portaria/saida': 'Saída de Animais',
  '/portaria/relatorio-entrada': 'Relatório de Entrada',
  '/portaria/relatorio-saida': 'Relatório de Saída',
  '/portaria/rastreamento': 'Rastreamento por Vendedor',
  '/relatorios': 'Relatórios',
  '/recibos': 'Recibos',
  '/meios-pagamento': 'Meios de Pagamento',
  '/comissoes': 'Comissões',
  '/leiloeiros': 'Leiloeiros',
  '/tipos-animal': 'Tipos de Animal',
  '/racas': 'Raças',
  '/idades': 'Idades',
  '/pisteiros': 'Pisteiros',
  '/configuracoes': 'Configurações',
  '/configuracoes/faturamento': 'Faturamento',
  '/configuracoes/usuarios': 'Usuários',
  '/configuracoes/catalogo-leilao': 'Config. Catálogo Leilão',
  '/configuracoes/catalogo-pisteiro': 'Config. Catálogo Pisteiro',
  '/configuracoes/catalogo-detalhado': 'Config. Catálogo Detalhado',
  '/configuracoes/ficha-leiloeiro': 'Config. Ficha Leiloeiro',
  '/configuracoes/contratos': 'Contratos',
  '/configuracoes/dados-bancarios': 'Dados Bancários',
  '/configuracoes/mapas': 'Config. Mapas',
  '/configuracoes/auditoria': 'Log de Auditoria',
  '/configuracoes/meus-dados': 'Meus Dados',
  '/organizadores': 'Organizadores',
  '/super-admin/importacao': 'Importar Leilão',
  '/super-admin/importacao-clientes': 'Importar Clientes',
  '/super-admin/importacao-leiloes-bulk': 'Importar Leilões em Lote',
  '/super-admin/dashboard': 'Painel Super Admin',
  '/super-admin/tokens-externos': 'Tokens Externos',
  '/super-admin/whatsapp': 'WhatsApp (2FA)',
  '/super-admin/todos-logins': 'Histórico de Logins',
  '/gestao': 'Gestão Financeira',
  '/socios': 'Gestão de Sócios',
  '/socios/relatorio': 'Relatório de Sócios',
  '/relatorios/receita-pisteiros': 'Receita Pisteiros',
  '/relatorios/carteira-pisteiros': 'Carteira de Pisteiros',
  '/gestao/dashboard': 'Dashboard Financeiro',
  '/gestao/contas-receber': 'Comissão a Receber',
  '/gestao/creditos-clientes': 'Créditos de Clientes',
  '/gestao/contas-pagar': 'Despesas do Leilão',
  '/gestao/categorias': 'Categorias',
  '/gestao/formas-pagamento': 'Formas de Pagamento',
  '/gestao/relatorio/contas-receber': 'Relatório Comissão a Receber',
  '/gestao/relatorio/contas-pagar': 'Relatório Despesas do Leilão',
  '/gestao/relatorio/geral': 'Relatório Financeiro Geral',
  '/gestao/relatorio/comprador-vendedor': 'Rel. Comprador x Vendedor',
  '/gestao/comprador-vendedor': 'Comprador x Vendedor',
  '/gestao/vendedor': 'Repasses ao Vendedor',
  '/gestao/relatorio/repasses-vendedor': 'Rel. Repasses ao Vendedor',
  '/gestao/configuracoes': 'Configurações da Gestão',
  '/inteligencia-pecuaria': 'Inteligência Pecuária',
}

// ─── Itens do menu ────────────────────────────────────────────────────────────
const MENU_ITEMS = [
  {
    icon: TrendingUp, label: 'Gestão', path: '/gestao', color: 'text-cyan-600',
    premium: 'gestao_financeira',
    badge: 'Premium',
    submenu: [
      // { icon: LayoutDashboard, label: 'Dashboard',             path: '/gestao/dashboard',                color: 'text-blue-500' },
      { section: 'LANÇAMENTOS' },
      { icon: DollarSign,      label: 'Comissão a Receber',   path: '/gestao/contas-receber',           color: 'text-green-500' },
      { icon: Coins,           label: 'Créditos de Clientes', path: '/gestao/creditos-clientes',        color: 'text-amber-600' },
      { icon: Wallet,          label: 'Despesas do Leilão',   path: '/gestao/contas-pagar',             color: 'text-red-500' },
      { icon: Users,           label: 'Comprador x Vendedor', path: '/gestao/comprador-vendedor',       color: 'text-amber-600', labelViaLeiloeira: 'Comprador (Pagto)', gestaoFluxoExclude: 'nenhum' },
      { icon: Send,            label: 'Vendedor (Repasses)',  path: '/gestao/vendedor',                 color: 'text-green-600', gestaoFluxoRequired: 'via_leiloeira' },
      { section: 'RELATÓRIOS' },
      { icon: FileText,        label: 'Comissão a Receber',   path: '/gestao/relatorio/contas-receber',       color: 'text-green-600' },
      { icon: FileText,        label: 'Despesas do Leilão',   path: '/gestao/relatorio/contas-pagar',         color: 'text-red-600' },
      { icon: FileText,        label: 'Comprador (Pgto)',     path: '/gestao/relatorio/comprador-vendedor',   color: 'text-amber-600', gestaoFluxoExclude: 'nenhum' },
      { icon: FileText,        label: 'Vendedor (Repasses)', path: '/gestao/relatorio/repasses-vendedor',    color: 'text-green-600', gestaoFluxoRequired: 'via_leiloeira' },
      { icon: FileText,        label: 'Financeiro Geral',     path: '/gestao/relatorio/geral',                color: 'text-blue-600' },
      { section: 'CONFIGURAR' },
      { icon: Tag,             label: 'Categorias',           path: '/gestao/categorias',               color: 'text-purple-500' },
      { icon: CreditCard,      label: 'Formas de Pagamento',  path: '/gestao/formas-pagamento',         color: 'text-indigo-500' },
      { icon: Settings,        label: 'Configurações',        path: '/gestao/configuracoes',            color: 'text-slate-500' },
    ],
  },
  { icon: Brain, label: 'Inteligência Pecuária', path: '/inteligencia-pecuaria', color: 'text-purple-600', premium: 'inteligencia_pecuaria' },
  { icon: LayoutDashboard, label: 'Dashboard',       path: '/dashboard',     color: 'text-blue-500' },
  { icon: Users,           label: 'Clientes',        path: '/clientes',      color: 'text-emerald-500' },
  { icon: Users, label: 'Sócios', path: '/socios', color: 'text-purple-500', premium: 'gestao_socios',
    submenu: [
      { icon: Users,    label: 'Gestão de Sócios', path: '/socios',           color: 'text-purple-500' },
      { icon: FileText, label: 'Relatório',        path: '/socios/relatorio', color: 'text-purple-600' },
    ],
  },
  { icon: Gavel,           label: 'Leilões',         path: '/leiloes',       color: 'text-violet-500' },
  { icon: Package2,        label: 'Lotes',           path: '/lotes',         color: 'text-orange-500' },
  {
    icon: FileText, label: 'Ordem de Entrada', path: '/ordem-entrada', color: 'text-indigo-500',
    submenu: [
      { icon: FileText, label: 'Ordem',                path: '/ordem-entrada',                       color: 'text-indigo-500' },
      { icon: Send,     label: 'Filtros Automáticos',  path: '/ordem-entrada/filtros-automaticos',   color: 'text-purple-500', premiumRequired: 'filtros_baterias' },
      { icon: FileText, label: 'Justificativas',       path: '/ordem-entrada/justificativas',         color: 'text-indigo-400', premiumRequired: 'filtros_baterias' },
    ],
  },
  {
    icon: FileText, label: 'Catálogos', path: '/catalogos', color: 'text-teal-500',
    submenu: [
      { icon: FileText,  label: 'Catálogo Leilão',    path: '/catalogos/leilao',         color: 'text-teal-500' },
      { icon: FileText,  label: 'Catálogo Pisteiro',  path: '/catalogos/pisteiro',       color: 'text-violet-500' },
      { icon: FileText,  label: 'Catálogo Detalhado', path: '/catalogos/detalhado',      color: 'text-emerald-500' },
      { icon: UserCog,   label: 'Ficha do Leiloeiro', path: '/catalogos/ficha-leiloeiro',color: 'text-orange-500' },
      { icon: FileText,  label: 'Ficha do Curral',    path: '/catalogos/ficha-curral',  color: 'text-teal-500' },
      { icon: Globe,     label: 'Catálogo Online',    path: '/catalogos/online',         color: 'text-teal-500' },
      { icon: FileText,  label: 'Nota Compromisso',   path: '/catalogos/nota-compromisso', color: 'text-amber-500' },
      { icon: FileText,  label: 'Cat. Transmissão',   path: '/catalogos/transmissao',       color: 'text-teal-600' },
    ]
  },
  {
    icon: Map, label: 'Mapas', path: '/mapas', color: 'text-cyan-500',
    submenu: [
      { icon: Map, label: 'Fatura do Comprador',    path: '/mapas/fatura-comprador',      color: 'text-blue-500',    submodulo: 'mapas-fatura-comprador' },
      { icon: Map, label: 'Fatura do Vendedor',     path: '/mapas/fatura-vendedor',       color: 'text-green-500',   submodulo: 'mapas-fatura-vendedor' },
      { icon: Map, label: 'Fatura Acerto',          path: '/mapas/fatura-acerto',         color: 'text-fuchsia-500', submodulo: 'mapas-fatura-acerto', fluxoRequired: 'comissao_cavalo' },
      { icon: Map, label: 'Mapa do Comprador',      path: '/mapas/comprador',             color: 'text-cyan-500',    submodulo: 'mapas-comprador', fluxoExclude: 'comissao_cavalo' },
      { icon: Map, label: 'Mapa do Vendedor',       path: '/mapas/vendedor',              color: 'text-emerald-500', submodulo: 'mapas-vendedor', fluxoExclude: 'comissao_cavalo' },
      { icon: Map, label: 'Mapa Geral',             path: '/mapas/mapa-geral',            color: 'text-indigo-500',  submodulo: 'mapas-geral' },
      { icon: Map, label: 'Mapa Geral Defesas',     path: '/mapas/mapa-geral-defesas',    color: 'text-orange-500',  submodulo: 'mapas-geral-defesas' },
      { icon: Map, label: 'Mapa Geral Não Mov.',    path: '/mapas/mapa-geral-naomov',     color: 'text-gray-500',    submodulo: 'mapas-geral-naomov' },
      { icon: Map, label: 'Mapa Geral Vendedores',  path: '/mapas/mapa-geral-vendedores', color: 'text-emerald-600', submodulo: 'mapas-geral-vendedores' },
      { icon: Map, label: 'Mapa Geral Compradores', path: '/mapas/mapa-geral-compradores',color: 'text-blue-600',    submodulo: 'mapas-geral-compradores' },
      { icon: Map, label: 'Vendedor (Defesas)',     path: '/mapas/vendedor-defesa',       color: 'text-orange-500',  submodulo: 'mapas-vendedor-defesa' },
    ]
  },
  {
    icon: BarChart3, label: 'Relatórios', path: '/relatorios', color: 'text-rose-500',
    submenu: [
      { icon: FileText,   label: 'Autorização GTA',       path: '/relatorios/autorizacao-gta',      color: 'text-teal-600',    submodulo: 'relatorios-autorizacao-gta' },
      { icon: BarChart3,  label: 'Balanço de Vendas',     path: '/relatorios/balanco-vendas',       color: 'text-rose-500',    submodulo: 'relatorios-balanco-vendas' },
      { icon: UserCog,    label: 'Comissão Leiloeiro',    path: '/relatorios/comissao-leiloeiro',   color: 'text-cyan-500',    submodulo: 'relatorios-comissao-leiloeiro' },
      { icon: Users,      label: 'Comissões por Cliente', path: '/relatorios/comissoes-cliente',    color: 'text-emerald-500', submodulo: 'relatorios-comissoes-cliente' },
      { icon: FileText,   label: 'Contrato Unificado',    path: '/relatorios/contrato-unificado',   color: 'text-violet-600',  submodulo: 'relatorios-contrato-unificado' },
      { icon: Truck,      label: 'Liberação Curral',      path: '/relatorios/liberacao-curral',     color: 'text-green-600',   submodulo: 'relatorios-liberacao-curral' },
      { icon: DollarSign, label: 'Fretes',                path: '/relatorios/fretes',               color: 'text-yellow-600',  submodulo: 'relatorios-fretes' },
      { icon: Users,      label: 'Lista de Compradores',  path: '/relatorios/listagem-compradores', color: 'text-blue-500',    submodulo: 'relatorios-compradores' },
      { icon: Users,      label: 'Lista de Vendedores',   path: '/relatorios/lista-vendedores',     color: 'text-blue-500',    submodulo: 'relatorios-vendedores' },
      { icon: BarChart3,  label: 'Ranking Compradores',   path: '/relatorios/ranking-compradores',  color: 'text-amber-500',   submodulo: 'relatorios-compradores' },
      { icon: BarChart3,  label: 'Ranking Vendedores',    path: '/relatorios/ranking-vendedores',   color: 'text-amber-500',   submodulo: 'relatorios-vendedores' },
      { icon: Users,      label: 'Relação Compradores',   path: '/relatorios/relacao-compradores',  color: 'text-blue-500',    submodulo: 'relatorios-compradores' },
      { icon: Users,      label: 'Relação Vendedores',    path: '/relatorios/relacao-vendedores',   color: 'text-emerald-500', submodulo: 'relatorios-vendedores' },
      { icon: Users,      label: 'Vendedores (Nominal)',  path: '/relatorios/relacao-vendedores-nominal', color: 'text-emerald-500', submodulo: 'relatorios-vendedores' },
      { icon: DollarSign, label: 'Receita Pisteiros',     path: '/relatorios/receita-pisteiros',    color: 'text-purple-500',  submodulo: 'relatorios-pisteiros', premiumRequired: 'quem_vendeu_trouxe' },
      { icon: Users,      label: 'Carteira Pisteiros',    path: '/relatorios/carteira-pisteiros',   color: 'text-purple-500',  submodulo: 'relatorios-pisteiros', premiumRequired: 'quem_vendeu_trouxe' },
      { icon: DollarSign, label: 'Distribuição Comissão', path: '/relatorios/distribuicao-comissao',color: 'text-indigo-500',  submodulo: 'relatorios-distribuicao-comissao' },
    ]
  },
  {
    icon: Truck, label: 'Entrada/Saída', path: '/portaria', color: 'text-green-600', premium: 'portaria',
    submenu: [
      { icon: Truck,    label: 'Entrada de Animais',    path: '/portaria/entrada',           color: 'text-green-600' },
      { icon: LogOut,   label: 'Saída de Animais',      path: '/portaria/saida',             color: 'text-orange-500' },
      { icon: FileText, label: 'Relatório de Entrada',  path: '/portaria/relatorio-entrada', color: 'text-teal-500' },
      { icon: FileText, label: 'Relatório de Saída',    path: '/portaria/relatorio-saida',   color: 'text-amber-500' },
      { icon: FileText, label: 'Rastreamento',          path: '/portaria/rastreamento',      color: 'text-purple-500' },
    ]
  },
  {
    icon: Settings, label: 'Cadastros', path: '/cadastros', color: 'text-gray-500',
    submenu: [
      { icon: Banknote,  label: 'Meios de Pagamento', path: '/cadastros/meios-pagamento', color: 'text-emerald-500' },
      { icon: Receipt,   label: 'Comissões',           path: '/comissoes',       color: 'text-emerald-500' },
      { icon: UserCog,   label: 'Leiloeiros',          path: '/leiloeiros',      color: 'text-cyan-500' },
      { icon: Beef,      label: 'Tipos de Animal',     path: '/tipos-animal',    color: 'text-amber-500' },
      { icon: Package2,  label: 'Raças',               path: '/racas',           color: 'text-violet-500' },
      { icon: Calendar,  label: 'Idades',              path: '/idades',          color: 'text-blue-500' },
      { icon: UserCog,   label: 'Pisteiros',           path: '/pisteiros',       color: 'text-orange-500', premiumRequired: 'quem_vendeu_trouxe' },
      { icon: FileText,  label: 'Taxas GTA/Nota',     path: '/cadastros/gta-nota', color: 'text-amber-600', fluxoRequired: 'gta_nota' },
      { icon: Truck,     label: 'Motoristas',         path: '/cadastros/motoristas', color: 'text-blue-500' },
      { icon: Users,     label: 'Promotores',         labelCavalo: 'Promotores (Leilão)', path: '/promotores', color: 'text-indigo-500', featureRequired: 'promotores_habilitado' },
      { icon: UserCog,   label: 'Assessores',         path: '/assessores', color: 'text-indigo-500', fluxoRequired: 'comissao_cavalo' },
      { icon: Beef,      label: 'Animais',            path: '/animais-cadastro', color: 'text-amber-500', fluxoRequired: 'comissao_cavalo' },
    ]
  },
  { icon: Receipt,   label: 'Recibos',       path: '/recibos',       color: 'text-emerald-500' },
  {
    icon: Cog, label: 'Configurações', path: '/configuracoes', color: 'text-slate-500',
    submenu: [
      { icon: FileText,  label: 'Catálogo Leilão',    path: '/configuracoes/catalogo-leilao',   color: 'text-violet-500', submodulo: 'configuracoes-catalogos' },
      { icon: Globe,     label: 'Catálogo Online',    path: '/configuracoes/catalogo-online',   color: 'text-teal-500',   submodulo: 'configuracoes-catalogos' },
      { icon: Link2,     label: 'Link de Cadastro',   path: '/configuracoes/cadastro-publico',  color: 'text-blue-500',   premiumRequired: 'link_cadastro' },
      { icon: FileText,  label: 'Catálogo Pisteiro',  path: '/configuracoes/catalogo-pisteiro', color: 'text-violet-500', submodulo: 'configuracoes-catalogos' },
      { icon: FileText,  label: 'Catálogo Detalhado', path: '/configuracoes/catalogo-detalhado',color: 'text-indigo-500', submodulo: 'configuracoes-catalogos' },
      { icon: FileText,  label: 'Contratos',          path: '/configuracoes/contratos',         color: 'text-emerald-500',submodulo: 'configuracoes-contratos' },
      { icon: CreditCard,label: 'Dados Bancários',    path: '/configuracoes/dados-bancarios',   color: 'text-blue-500',   submodulo: 'configuracoes-bancario' },
      { icon: Banknote,  label: 'Faturamento',        path: '/configuracoes/faturamento',       color: 'text-emerald-500',submodulo: 'configuracoes-faturamento' },
      { icon: FileText,  label: 'Ficha do Leiloeiro', path: '/configuracoes/ficha-leiloeiro',   color: 'text-orange-500', submodulo: 'configuracoes-catalogos' },
      { icon: Map,       label: 'Mapas',              path: '/configuracoes/mapas',             color: 'text-cyan-500',   submodulo: 'configuracoes-mapas' },
      { icon: FileText,  label: 'Relatórios',         path: '/configuracoes/relatorios',        color: 'text-purple-500', submodulo: 'configuracoes-relatorios' },
      { icon: UserCog,   label: 'Usuários',           path: '/configuracoes/usuarios',          color: 'text-blue-500',   submodulo: 'configuracoes-usuarios' },
      { icon: Building2, label: 'Meus Dados',          path: '/configuracoes/meus-dados',        color: 'text-blue-500' },
      { icon: FileText,  label: 'Log de Auditoria',   path: '/configuracoes/auditoria',         color: 'text-amber-500',  adminOnly: true },
      { icon: ShieldOff, label: 'Isenções Defesa',    path: '/configuracoes/isencao-defesa',    color: 'text-red-500',    premiumRequired: 'isencao_defesa', adminOnly: true },
    ]
  },
  { icon: Building2, label: 'Organizadores', path: '/organizadores', color: 'text-amber-500', admin: true },
  { icon: BarChart3, label: 'Painel Admin', path: '/super-admin/dashboard', color: 'text-red-500', admin: true },
  { icon: Key, label: 'Tokens Externos', path: '/super-admin/tokens-externos', color: 'text-emerald-500', admin: true },
  { icon: MessageCircle, label: 'WhatsApp (2FA)', path: '/super-admin/whatsapp', color: 'text-green-500', admin: true },
  { icon: History, label: 'Histórico de Logins', path: '/super-admin/todos-logins', color: 'text-indigo-500', admin: true },
  // { icon: FileText, label: 'Importar', path: '/super-admin/importar', color: 'text-emerald-500', admin: true,
  //   submenu: [
  //     { label: 'Importar Leilão',        path: '/super-admin/importacao' },
  //     { label: 'Importar Leilões em Lote', path: '/super-admin/importacao-leiloes-bulk' },
  //     { label: 'Importar Clientes',      path: '/super-admin/importacao-clientes' },
  //   ]
  // },
]

// ─── Verificação de acesso ───────────────────────────────────────────────────
function hasModuleAccess(item, user) {
  if (!user) return false
  if (user.permission_level === 'super_admin' || user.permission_level === 'admin') return true

  const moduleMap = {
    // Base
    '/dashboard':          'dashboard',
    '/leilao-tempo-real':  'leilao-tempo-real',
    '/clientes':           'clientes',
    '/leiloes':            'leiloes',
    '/lotes':              'lotes',
    '/catalogos':          'catalogos',
    '/recibos':            'recibos',
    // Relatórios (suporta array — qualquer sub-ID concede acesso ao pai)
    '/relatorios': ['relatorios','relatorios-autorizacao-gta','relatorios-balanco-vendas','relatorios-comissao-leiloeiro','relatorios-comissoes-cliente','relatorios-contrato-unificado','relatorios-liberacao-curral','relatorios-fretes','relatorios-compradores','relatorios-vendedores','relatorios-pisteiros','relatorios-distribuicao-comissao'],
    '/relatorios/autorizacao-gta':      ['relatorios', 'relatorios-autorizacao-gta'],
    '/relatorios/balanco-vendas':        ['relatorios', 'relatorios-balanco-vendas'],
    '/relatorios/comissao-leiloeiro':    ['relatorios', 'relatorios-comissao-leiloeiro'],
    '/relatorios/comissoes-cliente':     ['relatorios', 'relatorios-comissoes-cliente'],
    '/relatorios/contrato-unificado':    ['relatorios', 'relatorios-contrato-unificado'],
    '/relatorios/liberacao-curral':      ['relatorios', 'relatorios-liberacao-curral'],
    '/relatorios/fretes':                ['relatorios', 'relatorios-fretes'],
    '/relatorios/listagem-compradores':  ['relatorios', 'relatorios-compradores'],
    '/relatorios/relacao-compradores':   ['relatorios', 'relatorios-compradores'],
    '/relatorios/ranking-compradores':   ['relatorios', 'relatorios-compradores'],
    '/relatorios/lista-vendedores':      ['relatorios', 'relatorios-vendedores'],
    '/relatorios/relacao-vendedores':    ['relatorios', 'relatorios-vendedores'],
    '/relatorios/relacao-vendedores-nominal': ['relatorios', 'relatorios-vendedores'],
    '/relatorios/ranking-vendedores':    ['relatorios', 'relatorios-vendedores'],
    '/relatorios/receita-pisteiros':     ['relatorios', 'relatorios-pisteiros'],
    '/relatorios/carteira-pisteiros':    ['relatorios', 'relatorios-pisteiros'],
    '/relatorios/distribuicao-comissao': ['relatorios', 'relatorios-distribuicao-comissao'],
    // Configurações (suporta array — qualquer sub-ID concede acesso ao pai)
    '/configuracoes': ['configuracoes','configuracoes-catalogos','configuracoes-contratos','configuracoes-bancario','configuracoes-faturamento','configuracoes-mapas','configuracoes-relatorios','configuracoes-usuarios'],
    '/configuracoes/catalogo-leilao':    ['configuracoes', 'configuracoes-catalogos'],
    '/configuracoes/catalogo-online':    ['configuracoes', 'configuracoes-catalogos'],
    '/configuracoes/catalogo-pisteiro':  ['configuracoes', 'configuracoes-catalogos'],
    '/configuracoes/catalogo-detalhado': ['configuracoes', 'configuracoes-catalogos'],
    '/configuracoes/ficha-leiloeiro':    ['configuracoes', 'configuracoes-catalogos'],
    '/configuracoes/contratos':          ['configuracoes', 'configuracoes-contratos'],
    '/configuracoes/dados-bancarios':    ['configuracoes', 'configuracoes-bancario'],
    '/configuracoes/faturamento':        ['configuracoes', 'configuracoes-faturamento'],
    '/configuracoes/mapas':              ['configuracoes', 'configuracoes-mapas'],
    '/configuracoes/relatorios':         ['configuracoes', 'configuracoes-relatorios'],
    '/configuracoes/usuarios':           ['configuracoes', 'configuracoes-usuarios'],
    '/configuracoes/meus-dados':         null, // acessível a todos os perfis
    // Mapas e submenus (suporta array — qualquer ID da lista concede acesso)
    // 'mapas' = acesso total (legado); sub-IDs = acesso granular por sub-item
    '/mapas':                       ['mapas', 'mapas-fatura-comprador', 'mapas-fatura-vendedor', 'mapas-fatura-acerto', 'mapas-comprador', 'mapas-vendedor', 'mapas-geral', 'mapas-vendedor-defesa'],
    '/mapas/fatura-comprador':      ['mapas', 'mapas-fatura-comprador'],
    '/mapas/fatura-vendedor':       ['mapas', 'mapas-fatura-vendedor'],
    '/mapas/fatura-acerto':         ['mapas', 'mapas-fatura-acerto'],
    '/mapas/comprador':             ['mapas', 'mapas-comprador'],
    '/mapas/vendedor':              ['mapas', 'mapas-vendedor'],
    '/mapas/vendedor-defesa':       ['mapas', 'mapas-vendedor-defesa'],
    '/mapas/mapa-geral':            ['mapas', 'mapas-geral'],
    '/mapas/mapa-geral-defesas':    ['mapas', 'mapas-geral'],
    '/mapas/mapa-geral-naomov':     ['mapas', 'mapas-geral'],
    '/mapas/mapa-geral-vendedores': ['mapas', 'mapas-geral'],
    '/mapas/mapa-geral-compradores':['mapas', 'mapas-geral'],
    // Portaria e submenus
    '/portaria':                    'portaria',
    '/portaria/entrada':            'portaria',
    '/portaria/saida':              'portaria',
    '/portaria/relatorio-entrada':  'portaria',
    '/portaria/relatorio-saida':    'portaria',
    '/portaria/rastreamento':       'portaria',
    // Cadastros
    '/comissoes':        'comissoes',
    '/leiloeiros':       'leiloeiros',
    '/meios-pagamento':  'meios-pagamento',
    '/tipos-animal':     'tipos-animal',
    '/racas':            'racas',
    '/idades':           'idades',
    '/pisteiros':        'pisteiros',
    // Premium: Gestão Financeira (suporta array — qualquer ID da lista concede acesso)
    // 'gestao' = ID legado (acesso total), mantido para compatibilidade retroativa
    '/gestao':                               ['gestao', 'gestao-contas-receber', 'gestao-contas-pagar', 'gestao-comprador-vendedor', 'gestao-categorias', 'gestao-formas-pagamento'],
    '/gestao/dashboard':                     ['gestao', 'gestao-contas-receber', 'gestao-contas-pagar', 'gestao-comprador-vendedor', 'gestao-categorias', 'gestao-formas-pagamento'],
    '/gestao/relatorio/geral':               ['gestao', 'gestao-contas-receber', 'gestao-contas-pagar', 'gestao-comprador-vendedor', 'gestao-categorias', 'gestao-formas-pagamento'],
    '/gestao/categorias':                    ['gestao', 'gestao-categorias'],
    '/gestao/formas-pagamento':              ['gestao', 'gestao-formas-pagamento'],
    '/gestao/contas-receber':                ['gestao', 'gestao-contas-receber'],
    '/gestao/creditos-clientes':             ['gestao', 'gestao-contas-receber'],
    '/gestao/relatorio/contas-receber':      ['gestao', 'gestao-contas-receber'],
    '/gestao/contas-pagar':                  ['gestao', 'gestao-contas-pagar'],
    '/gestao/relatorio/contas-pagar':        ['gestao', 'gestao-contas-pagar'],
    '/gestao/comprador-vendedor':            ['gestao', 'gestao-comprador-vendedor'],
    '/gestao/vendedor':                      ['gestao', 'gestao-comprador-vendedor'],
    '/gestao/relatorio/repasses-vendedor':   ['gestao', 'gestao-comprador-vendedor'],
    '/gestao/relatorio/comprador-vendedor':  ['gestao', 'gestao-comprador-vendedor'],
    '/gestao/configuracoes':                 ['gestao', 'gestao-categorias', 'gestao-formas-pagamento', 'gestao-comprador-vendedor'],
    // Premium: Sócios
    '/socios':           'socios',
    '/socios/relatorio': 'socios',
    // Premium: Inteligência Pecuária
    '/inteligencia-pecuaria': 'inteligencia-pecuaria',
    // Ordem de Entrada e submenus
    '/ordem-entrada':                          'ordem-entrada',
    '/ordem-entrada/filtros-automaticos':      'ordem-entrada',
    '/ordem-entrada/filtros-automaticos/novo': 'ordem-entrada',
    '/ordem-entrada/justificativas':           'ordem-entrada',
  }

  if (user.permission_level === 'manejo') {
    return ['/dashboard', '/lotes', '/clientes', '/relatorios'].includes(item.path)
  }
  if (user.permission_level === 'transmissao') {
    return ['/catalogos'].includes(item.path)
  }
  if (user.permission_level === 'escritorio') {
    if (item.path === '/configuracoes') {
      return (item.submenu?.filter(s => s.path !== '/configuracoes/usuarios') || []).length > 0
    }
    if (item.path === '/configuracoes/usuarios') return false
    return true
  }
  if (user.permission_level === 'personalizado') {
    const m = [...(user.modulos_acesso || [])]
    // Ordem de Entrada premium vem como objeto { modulo: 'ordem_entrada', visualizar, ... }, não como string
    const oe = m.find(x => x?.modulo === 'ordem_entrada')
    if (oe && (oe.visualizar || oe.editar_filtros || oe.mover_manual)) m.push('ordem-entrada')
    const checkIds = (val) => {
      if (!val) return false
      const ids = Array.isArray(val) ? val : [val]
      return ids.some(id => m.includes(id) || m.includes(`${id}-visualizar`))
    }
    const moduleId = moduleMap[item.path]
    if (moduleId === null) return true  // null = livre para todos os perfis
    if (moduleId) return checkIds(moduleId)
    if (item.submenu) return item.submenu.some(s => checkIds(moduleMap[s.path]))
  }
  return false
}

// ─── Dropdown de organizadores com pesquisa debounce ──────────────────────────
function OrgDropdown({ organizadores, organizadorAtivo, isGlobalView, onSelect, onClear, onClose, mobile }) {
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const inputRef = useRef(null)

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 200)
    return () => clearTimeout(timer)
  }, [search])

  useEffect(() => {
    // Auto-focus apenas no desktop (no mobile o teclado não abre automaticamente)
    if (!mobile) setTimeout(() => inputRef.current?.focus(), 50)
  }, [])

  const filtered = debouncedSearch.trim()
    ? organizadores.filter(org => org.name.toLowerCase().includes(debouncedSearch.toLowerCase()))
    : organizadores

  // Mobile: portal direto no body para evitar bloqueio de touch events pelo overflow:hidden do sidebar
  if (mobile) {
    return createPortal(
      <div className="fixed inset-0 z-[9999] bg-black/60 flex items-end justify-center">
        <div className="bg-slate-800 w-full max-h-[75vh] rounded-t-2xl flex flex-col" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700 flex-shrink-0">
            <span className="text-sm font-semibold text-slate-200">Trocar Organizador</span>
            <button onTouchEnd={(e) => { e.preventDefault(); onClose() }} onClick={onClose} className="text-slate-400 hover:text-white text-lg px-2">✕</button>
          </div>
          <div className="px-3 py-2 flex-shrink-0">
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Pesquisar organizador..."
              className="w-full px-3 py-2 text-sm bg-slate-700 border border-slate-600 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex-1 overflow-y-auto px-1 pb-3" style={{ WebkitOverflowScrolling: 'touch' }}>
            <button
              onTouchEnd={(e) => { e.preventDefault(); onClear() }}
              onClick={onClear}
              className={`w-full flex items-center gap-3 px-4 py-3 text-sm rounded-lg transition-colors ${isGlobalView ? 'text-blue-300 bg-slate-700/50 font-semibold' : 'text-slate-400 hover:bg-slate-700'}`}
            >
              <div className="w-2.5 h-2.5 rounded-full bg-blue-400" />
              <span>Visão Global</span>
              {isGlobalView && <span className="ml-auto text-blue-400">✓</span>}
            </button>
            <div className="border-t border-slate-700 my-1" />
            {filtered.length === 0 ? (
              <div className="text-center py-4 text-slate-500 text-sm">Nenhum organizador encontrado</div>
            ) : (
              filtered.map(org => (
                <button
                  key={org.id}
                  onTouchEnd={(e) => { e.preventDefault(); onSelect(org) }}
                  onClick={() => onSelect(org)}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-sm rounded-lg transition-colors ${organizadorAtivo?.id === org.id ? 'text-blue-300 bg-slate-700/50 font-semibold' : 'text-slate-400 hover:bg-slate-700'}`}
                >
                  <div className={`w-2.5 h-2.5 rounded-full ${org.is_active ? 'bg-emerald-400' : 'bg-slate-600'}`} />
                  <span className="truncate">{org.name}</span>
                  {organizadorAtivo?.id === org.id && <span className="ml-auto text-blue-400">✓</span>}
                </button>
              ))
            )}
          </div>
        </div>
      </div>,
      document.body
    )
  }

  // Desktop: dropdown inline
  return (
    <div className="mt-1 bg-slate-800 border border-slate-600 rounded-lg shadow-lg z-50 overflow-hidden">
      <div className="px-2 py-1.5 border-b border-slate-700">
        <input
          ref={inputRef}
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Pesquisar..."
          className="w-full px-2 py-1 text-xs bg-slate-700 border border-slate-600 rounded text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
      </div>
      <div className="max-h-52 overflow-y-auto">
        <button
          onClick={onClear}
          className={`w-full flex items-center gap-2 px-3 py-2 text-xs hover:bg-slate-700 transition-colors ${isGlobalView ? 'text-blue-300 font-semibold' : 'text-slate-400'}`}
        >
          <div className="w-2 h-2 rounded-full bg-blue-400" />
          <span>Visão Global</span>
          {isGlobalView && <span className="ml-auto">✓</span>}
        </button>
        <div className="border-t border-slate-700" />
        {filtered.length === 0 ? (
          <div className="text-center py-3 text-slate-500 text-xs">Nenhum encontrado</div>
        ) : (
          filtered.map(org => (
            <button
              key={org.id}
              onClick={() => onSelect(org)}
              className={`w-full flex items-center gap-2 px-3 py-2 text-xs hover:bg-slate-700 transition-colors ${organizadorAtivo?.id === org.id ? 'text-blue-300 font-semibold' : 'text-slate-400'}`}
            >
              <div className={`w-2 h-2 rounded-full ${org.is_active ? 'bg-emerald-400' : 'bg-slate-600'}`} />
              <span className="truncate">{org.name}</span>
              {organizadorAtivo?.id === org.id && <span className="ml-auto">✓</span>}
            </button>
          ))
        )}
      </div>
    </div>
  )
}

// ─── Componente de item do menu ───────────────────────────────────────────────
function NavItem({ item, collapsed, expandedMenus, toggleSubmenu, location, onNavigate }) {
  const Icon = item.icon
  const itemRef = useRef(null)
  const isSubActive = item.submenu && item.submenu.some(s => location.pathname === s.path)
  const isActive = location.pathname === item.path && !isSubActive
  const hasSubmenu = item.submenu?.length > 0
  const isExpanded = expandedMenus[item.path]

  // Scroll into view when expanded
  useEffect(() => {
    if (!isExpanded) return
    const el = itemRef.current
    if (!el) return
    const t = setTimeout(() => {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }, 50)
    return () => clearTimeout(t)
  }, [isExpanded])

  if (hasSubmenu) {
    return (
      <div ref={itemRef}>
        <button
          onClick={() => !collapsed && toggleSubmenu(item.path)}
          title={collapsed ? item.label : undefined}
          className={`nav-item ${isActive ? 'nav-item-active' : ''}`}
        >
          <Icon className={`nav-icon ${isActive ? 'text-white' : item.color}`} />
          {!collapsed && (
            <>
              <span className="nav-label">{item.label}</span>
              {item.badge && (
                <span className="ml-1 px-1.5 py-0.5 text-[10px] font-bold bg-green-500 text-white rounded-full leading-none">
                  {item.badge}
                </span>
              )}
              <span className="ml-auto text-current opacity-60">
                {isExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
              </span>
            </>
          )}
          {collapsed && <span className="nav-tooltip">{item.label}</span>}
        </button>

        {hasSubmenu && isExpanded && !collapsed && (
          <div className="submenu-container">
            {item.submenu.map((sub, idx) => {
              if (sub.section) {
                return (
                  <div key={`section-${idx}`} className="px-4 pt-3 pb-1">
                    <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">{sub.section}</span>
                  </div>
                )
              }
              const isSubActive = location.pathname === sub.path
              return (
                <Link
                  key={sub.path}
                  to={sub.path}
                  onClick={onNavigate}
                  className={`submenu-item ${isSubActive ? 'submenu-item-active' : ''}`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${isSubActive ? 'bg-rematei-blue' : 'bg-gray-300'}`} />
                  <span>{sub.label}</span>
                </Link>
              )
            })}
          </div>
        )}
      </div>
    )
  }

  return (
    <Link
      to={item.path}
      onClick={onNavigate}
      title={collapsed ? item.label : undefined}
      className={`nav-item ${isActive ? 'nav-item-active' : ''}`}
    >
      <Icon className={`nav-icon ${isActive ? 'text-white' : item.color}`} />
      {!collapsed && <span className="nav-label">{item.label}</span>}
      {collapsed && <span className="nav-tooltip">{item.label}</span>}
    </Link>
  )
}

// ─── Avatar do usuário ────────────────────────────────────────────────────────
function UserAvatar({ name, size = 'md' }) {
  const initial = name?.charAt(0)?.toUpperCase() || 'U'
  const cls = size === 'sm'
    ? 'w-7 h-7 text-xs'
    : size === 'lg'
    ? 'w-11 h-11 text-base'
    : 'w-9 h-9 text-sm'
  return (
    <div className={`${cls} rounded-full bg-gradient-to-br from-rematei-blue to-blue-700 flex items-center justify-center text-white font-semibold shadow-sm flex-shrink-0`}>
      {initial}
    </div>
  )
}

// ─── Layout principal ─────────────────────────────────────────────────────────
function LayoutContent({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [expandedMenus, setExpandedMenus] = useState({})
  const [showOrgDropdown, setShowOrgDropdown] = useState(false)
  const [showUserMenu, setShowUserMenu] = useState(false)
  const orgRef = useRef(null)
  const userRef = useRef(null)
  const location = useLocation()
  const { user, logout } = useAuthStore()
  const { organizadorAtivo, organizadores, setOrganizadores, switchToOrganizador, clearOrganizador, isGlobalView } = useOrganizador()

  // Título da página atual
  const pageTitle = ROUTE_TITLES[location.pathname] ||
    Object.entries(ROUTE_TITLES).find(([k]) => location.pathname.startsWith(k + '/'))?.[1] ||
    'Painel'

  const toggleSubmenu = (path) =>
    setExpandedMenus(prev => {
      // Se está abrindo este menu, fecha todos os outros
      if (!prev[path]) {
        return { [path]: true };
      }
      // Se está fechando, apenas fecha
      return { ...prev, [path]: false };
    })

  // Fechar mobile sidebar ao navegar
  const handleNavigate = () => setSidebarOpen(false)

  // Heartbeat de presença — marca "online agora" a cada 60s enquanto o app está aberto
  useEffect(() => {
    if (!user) return
    const enviarHeartbeat = () => { api.post('/auth/heartbeat').catch(() => {}) }
    enviarHeartbeat()
    const interval = setInterval(enviarHeartbeat, 60000)
    return () => clearInterval(interval)
  }, [user])

  // Fechar dropdowns ao clicar fora
  useEffect(() => {
    const handler = (e) => {
      if (orgRef.current && !orgRef.current.contains(e.target)) setShowOrgDropdown(false)
      if (userRef.current && !userRef.current.contains(e.target)) setShowUserMenu(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  // Fechar sidebar mobile e menus não ativos ao mudar de rota
  useEffect(() => {
    setSidebarOpen(false)
    setExpandedMenus(prev => {
      const next = {}
      Object.entries(prev).forEach(([path, open]) => {
        if (open && location.pathname.startsWith(path)) next[path] = true
      })
      return next
    })
  }, [location.pathname])

  // ── Notificações financeiras ──────────────────────────────────────────────
  const [showNotificacoes, setShowNotificacoes] = useState(false)
  const [notificacoes, setNotificacoes] = useState(null)
  const notifRef = useRef(null)
  const navigate = useNavigate()

  const temGestao = organizadorAtivo?.produtos_premium?.includes('gestao_financeira')

  // Verifica se há notificações não lidas
  const temNotificacoesNovas = (() => {
    if (!notificacoes || notificacoes.total_vencidas === 0) return false
    // Se nunca marcou como lidas, tem novas
    if (!notificacoes.lidas_em) return true
    // Se o total mudou desde que marcou como lidas, tem novas
    if (notificacoes.lidas_total !== notificacoes.total_vencidas) return true
    return false
  })()

  const marcarTodasComoLidas = async () => {
    if (!organizadorAtivo?.id) return
    try {
      const { gestaoService } = await import('../../services/gestaoService')
      await gestaoService.marcarNotificacoesLidas(organizadorAtivo.id)
      // Atualiza o state local para refletir que foram lidas (total atual = lidas_total)
      setNotificacoes(prev => prev ? { 
        ...prev, 
        lidas_em: new Date().toISOString(),
        lidas_total: prev.total_vencidas 
      } : prev)
      setShowNotificacoes(false)
    } catch { /* silencioso */ }
  }

  // ── Configuração da gestão (fluxo de pagamento) ───────────────────────────
  const [gestaoConfig, setGestaoConfig] = useState(null)

  useEffect(() => {
    if (!organizadorAtivo?.id || !temGestao) { setGestaoConfig(null); return }
    const carregar = async () => {
      try {
        const { gestaoService } = await import('../../services/gestaoService')
        const res = await gestaoService.obterConfiguracoes(organizadorAtivo.id)
        setGestaoConfig(res.data || res)
      } catch { /* silencioso */ }
    }
    carregar()
  }, [organizadorAtivo?.id, temGestao])

  useEffect(() => {
    if (!organizadorAtivo?.id || !temGestao) return
    const carregarNotificacoes = async () => {
      try {
        const { gestaoService } = await import('../../services/gestaoService')
        const res = await gestaoService.notificacoes(organizadorAtivo.id)
        setNotificacoes(res.data || res)
      } catch { /* silencioso */ }
    }
    carregarNotificacoes()
    const interval = setInterval(carregarNotificacoes, 5 * 60 * 1000) // atualiza a cada 5 min
    return () => clearInterval(interval)
  }, [organizadorAtivo?.id, temGestao])

  // Fechar dropdown de notificações ao clicar fora
  useEffect(() => {
    const handler = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) setShowNotificacoes(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  // Carregar organizadores (super_admin)
  useEffect(() => {
    if (user?.permission_level === 'super_admin') {
      organizadoresService.getAll({ per_page: 100 })
        .then(r => setOrganizadores(r.data || []))
        .catch(() => {})
    }
  }, [user])

  const visibleMenuItems = MENU_ITEMS.filter(item => {
    if (item.admin && user?.permission_level !== 'super_admin') return false
    if (item.premium) {
      const produtos = organizadorAtivo?.produtos_premium || []
      if (!produtos.includes(item.premium)) return false
    }
    return hasModuleAccess(item, user)
  }).map(item => {
    // Filtra subitens condicionais (fluxoRequired)
    if (item.submenu) {
      const filteredSubmenu = item.submenu.filter(sub => {
          if (sub.section) return true
          if (sub.fluxoRequired && organizadorAtivo?.fluxo_leilao !== sub.fluxoRequired) return false
          if (sub.fluxoExclude && organizadorAtivo?.fluxo_leilao === sub.fluxoExclude) return false
          if (sub.gestaoFluxoRequired && gestaoConfig?.fluxo_pagamento !== sub.gestaoFluxoRequired) return false
          if (sub.gestaoFluxoExclude && gestaoConfig?.fluxo_pagamento === sub.gestaoFluxoExclude) return false
          if (sub.premiumRequired && !(organizadorAtivo?.produtos_premium || []).includes(sub.premiumRequired)) return false
          if (sub.featureRequired && !organizadorAtivo?.[sub.featureRequired]) return false
          if (sub.adminOnly && user?.permission_level !== 'admin' && user?.permission_level !== 'super_admin') return false
          // Sub-module access check for personalizado users
          if (user?.permission_level === 'personalizado' && sub.submodulo) {
            const m = user.modulos_acesso || []
            const parentKey = sub.submodulo.split('-')[0]
            // Also check parent submodule (e.g. 'mapas-geral' grants access to 'mapas-geral-defesas')
            const parts = sub.submodulo.split('-')
            const parentSubmodulo = parts.length > 2 ? parts.slice(0, 2).join('-') : null
            if (!m.includes(parentKey) && !m.includes(sub.submodulo) && !m.includes(`${sub.submodulo}-visualizar`) && !(parentSubmodulo && m.includes(parentSubmodulo))) return false
          }
          return true
        }).map(sub => {
          if (sub.section) return sub
          // Label dinâmico baseado no fluxo da gestão
          if (sub.labelViaLeiloeira && gestaoConfig?.fluxo_pagamento === 'via_leiloeira') {
            return { ...sub, label: sub.labelViaLeiloeira }
          }
          // Label dinâmico pro fluxo cavalo (ex: "Promotores" → "Assessoria")
          if (sub.labelCavalo && organizadorAtivo?.fluxo_leilao === 'comissao_cavalo') {
            return { ...sub, label: sub.labelCavalo }
          }
          return sub
        })

      // Se após o filtro só sobra 1 item real (sem sections), remove o submenu e vai direto
      const realItems = filteredSubmenu.filter(s => !s.section)
      if (realItems.length === 1) {
        return { ...item, path: realItems[0].path, submenu: undefined }
      }
      if (realItems.length === 0) {
        return { ...item, submenu: undefined }
      }

      return { ...item, submenu: filteredSubmenu }
    }
    return item
  })

  // ── Sidebar (compartilhado entre desktop e mobile) ──────────────────────────
  const SidebarContent = ({ mobile = false }) => (
    <div className={`flex flex-col h-full sidebar-dark ${mobile ? 'bg-slate-900' : 'sidebar-desktop-bg'}`}>

      {/* Logo + Collapse toggle */}
      <div className="flex items-center justify-between px-3 py-3 border-b border-slate-700/50">
        <div className="flex items-center gap-3">
          <img
            src="/assets/rematei.png"
            alt="Rematei"
            className={`object-contain transition-all duration-200 ${sidebarCollapsed && !mobile ? 'w-8 h-8' : 'w-10 h-10'}`}
          />
          {(!sidebarCollapsed || mobile) && (
            <div>
              <p className="text-sm font-bold text-white leading-tight">Rematei</p>
              <p className="text-[10px] text-slate-400 leading-tight">Gestão de Leilões</p>
            </div>
          )}
        </div>
        {!mobile && (
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="p-1.5 rounded-lg text-slate-500 hover:text-slate-300 hover:bg-slate-700/60 transition-colors"
            title={sidebarCollapsed ? 'Expandir' : 'Recolher'}
          >
            {sidebarCollapsed ? <PanelLeftOpen className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
          </button>
        )}
        {mobile && (
          <button onClick={() => setSidebarOpen(false)} className="p-1.5 rounded-lg text-slate-500 hover:text-slate-300 hover:bg-slate-700/60 transition-colors">
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Org switcher (super_admin) */}
      {user?.permission_level === 'super_admin' && (!sidebarCollapsed || mobile) && (
        <div className="px-3 py-2 border-b border-slate-700/50" ref={!mobile ? orgRef : undefined}>
          <button
            onClick={() => setShowOrgDropdown(!showOrgDropdown)}
            className="w-full flex items-center justify-between gap-2 px-3 py-2 text-xs rounded-lg bg-slate-700/50 hover:bg-slate-700 transition-colors border border-slate-600/50"
          >
            <div className="flex items-center gap-2 min-w-0">
              <div className={`w-2 h-2 rounded-full flex-shrink-0 ${organizadorAtivo ? 'bg-emerald-400' : 'bg-blue-400'}`} />
              <span className="truncate text-slate-300 font-medium">
                {organizadorAtivo ? organizadorAtivo.name : 'Visão Global'}
              </span>
            </div>
            <ChevronDown className={`w-3.5 h-3.5 text-slate-500 flex-shrink-0 transition-transform duration-200 ${showOrgDropdown ? 'rotate-180' : ''}`} />
          </button>

          {showOrgDropdown && (
            <OrgDropdown
              organizadores={organizadores}
              organizadorAtivo={organizadorAtivo}
              isGlobalView={isGlobalView}
              onSelect={(org) => { switchToOrganizador(org); setShowOrgDropdown(false); if (mobile) setSidebarOpen(false) }}
              onClear={() => { clearOrganizador(); setShowOrgDropdown(false); if (mobile) setSidebarOpen(false) }}
              onClose={() => setShowOrgDropdown(false)}
              mobile={mobile}
            />
          )}
        </div>
      )}

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-0.5">
        {visibleMenuItems.map((item, idx) => (
          <div key={item.path}>
            {item.admin && !visibleMenuItems[idx - 1]?.admin && <div className="border-t border-gray-100 my-2 mx-1" />}
            <NavItem
              item={item}
              collapsed={sidebarCollapsed && !mobile}
              expandedMenus={expandedMenus}
              toggleSubmenu={toggleSubmenu}
              location={location}
              onNavigate={handleNavigate}
            />
          </div>
        ))}
      </nav>

      {/* User info */}
      <div className="flex-shrink-0 p-3 border-t border-slate-700/50">
        {!sidebarCollapsed || mobile ? (
          <div className="flex items-center gap-2.5">
            <UserAvatar name={user?.name} />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-slate-200 truncate">{user?.name || 'Usuário'}</p>
              <p className="text-[10px] text-slate-500 truncate">{user?.email}</p>
            </div>
            <button
              onClick={logout}
              className="p-1.5 text-slate-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
              title="Sair"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div className="flex justify-center">
            <button
              onClick={logout}
              className="p-2 text-slate-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors relative group"
              title="Sair"
            >
              <LogOut className="w-4 h-4" />
              <span className="nav-tooltip">Sair</span>
            </button>
          </div>
        )}
      </div>
    </div>
  )

  return (
    <SidebarContext.Provider value={{ sidebarOpen, setSidebarOpen }}>
      <div className="flex h-screen bg-gray-50 overflow-hidden">

        {/* ── Sidebar Desktop ──────────────────────────────────────── */}
        <aside className={`no-print hidden md:flex flex-col flex-shrink-0 transition-all duration-300 ease-in-out border-r border-gray-200 bg-white shadow-sm ${sidebarCollapsed ? 'w-[68px]' : 'w-60'}`}>
          <SidebarContent mobile={false} />
        </aside>

        {/* ── Sidebar Mobile (overlay) ─────────────────────────────── */}
        {sidebarOpen && (
          <div className="no-print fixed inset-0 z-50 md:hidden">
            {/* Backdrop */}
            <div
              className="absolute inset-0 bg-gray-900/50 backdrop-blur-sm"
              onClick={() => setSidebarOpen(false)}
            />
            {/* Panel */}
            <aside className="relative w-72 max-w-[85vw] h-full bg-white shadow-2xl flex flex-col overflow-hidden animate-slide-in-left">
              <SidebarContent mobile={true} />
            </aside>
          </div>
        )}

        {/* ── Conteúdo principal ───────────────────────────────────── */}
        <div className="flex flex-col flex-1 min-w-0 overflow-hidden">

          {/* Top Header */}
          <header className="no-print flex-shrink-0 h-12 bg-white border-b border-gray-200 shadow-sm z-10 relative flex items-center px-4">

            {/* Esquerda: hamburger (mobile) | org name (desktop) */}
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <button
                onClick={() => setSidebarOpen(true)}
                className="md:hidden p-2 -ml-1 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors flex-shrink-0"
              >
                <Menu className="w-5 h-5" />
              </button>

              {/* Desktop: org ativa */}
              {organizadorAtivo && (
                <p className="text-sm text-gray-500 truncate hidden md:block">
                  {organizadorAtivo.name}
                </p>
              )}
            </div>

            {/* Centro: logo do cliente (mobile only, absolutamente centralizado) */}
            <div className="absolute left-1/2 -translate-x-1/2 md:hidden flex items-center justify-center">
              {organizadorAtivo?.logo_url ? (
                <img
                  src={organizadorAtivo.logo_url}
                  alt={organizadorAtivo.name}
                  className="h-8 max-w-[150px] object-contain"
                />
              ) : (
                <img
                  src="/assets/rematei.png"
                  alt="Rematei"
                  className="h-8 w-8 object-contain"
                />
              )}
            </div>

            {/* Direita: notificações + user */}
            <div className="flex items-center gap-1.5 flex-shrink-0" ref={userRef}>
              <div className="relative" ref={notifRef}>
                <button
                  onClick={() => setShowNotificacoes(!showNotificacoes)}
                  className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors relative"
                >
                  <Bell className="w-4 h-4" />
                  {temNotificacoesNovas && (
                    <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                      {notificacoes.total_vencidas > 9 ? '9+' : notificacoes.total_vencidas}
                    </span>
                  )}
                </button>

                {showNotificacoes && (
                  <div className="absolute top-12 right-0 w-80 bg-white rounded-xl shadow-xl border border-gray-200 z-50 overflow-hidden">
                    <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
                      <h3 className="text-sm font-semibold text-gray-800">Notificações</h3>
                      {notificacoes && notificacoes.total_vencidas > 0 && (
                        <button
                          onClick={marcarTodasComoLidas}
                          className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                        >
                          Marcar como lidas
                        </button>
                      )}
                    </div>
                    <div className="py-2">
                      {!temGestao ? (
                        <div className="px-4 py-6 text-center text-sm text-gray-400">
                          Ative o Gerenciador Financeiro para receber notificações
                        </div>
                      ) : !notificacoes || notificacoes.total_vencidas === 0 ? (
                        <div className="px-4 py-6 text-center text-sm text-gray-400">
                          Nenhuma notificação no momento
                        </div>
                      ) : (
                        <div className="space-y-1">
                          {notificacoes.contas_receber_vencidas > 0 && (
                            <button
                              onClick={() => { navigate('/gestao/contas-receber'); setShowNotificacoes(false) }}
                              className="w-full flex items-start gap-3 px-4 py-3 hover:bg-red-50 transition-colors text-left"
                            >
                              <div className="flex-shrink-0 w-8 h-8 bg-red-100 rounded-full flex items-center justify-center mt-0.5">
                                <AlertTriangle className="w-4 h-4 text-red-600" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-800">
                                  {notificacoes.contas_receber_vencidas} conta{notificacoes.contas_receber_vencidas > 1 ? 's' : ''} a receber vencida{notificacoes.contas_receber_vencidas > 1 ? 's' : ''}
                                </p>
                                <p className="text-xs text-red-600 font-semibold">
                                  {Number(notificacoes.valor_receber_vencido).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                </p>
                              </div>
                              <ChevronRight className="w-4 h-4 text-gray-400 mt-1 flex-shrink-0" />
                            </button>
                          )}
                          {notificacoes.contas_pagar_vencidas > 0 && (
                            <button
                              onClick={() => { navigate('/gestao/contas-pagar'); setShowNotificacoes(false) }}
                              className="w-full flex items-start gap-3 px-4 py-3 hover:bg-orange-50 transition-colors text-left"
                            >
                              <div className="flex-shrink-0 w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center mt-0.5">
                                <AlertTriangle className="w-4 h-4 text-orange-600" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-800">
                                  {notificacoes.contas_pagar_vencidas} conta{notificacoes.contas_pagar_vencidas > 1 ? 's' : ''} a pagar vencida{notificacoes.contas_pagar_vencidas > 1 ? 's' : ''}
                                </p>
                                <p className="text-xs text-orange-600 font-semibold">
                                  {Number(notificacoes.valor_pagar_vencido).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                </p>
                              </div>
                              <ChevronRight className="w-4 h-4 text-gray-400 mt-1 flex-shrink-0" />
                            </button>
                          )}
                          {notificacoes.parcelas_negociacao_atrasadas > 0 && (
                            <button
                              onClick={() => { navigate('/gestao/comprador-vendedor'); setShowNotificacoes(false) }}
                              className="w-full flex items-start gap-3 px-4 py-3 hover:bg-amber-50 transition-colors text-left"
                            >
                              <div className="flex-shrink-0 w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center mt-0.5">
                                <AlertTriangle className="w-4 h-4 text-amber-600" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-800">
                                  {notificacoes.parcelas_negociacao_atrasadas} parcela{notificacoes.parcelas_negociacao_atrasadas > 1 ? 's' : ''} de negociação vencida{notificacoes.parcelas_negociacao_atrasadas > 1 ? 's' : ''}
                                </p>
                                <p className="text-xs text-amber-600 font-semibold">
                                  {Number(notificacoes.valor_parcelas_atrasado || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                </p>
                              </div>
                              <ChevronRight className="w-4 h-4 text-gray-400 mt-1 flex-shrink-0" />
                            </button>
                          )}
                          {notificacoes.repasses_pendentes > 0 && (
                            <button
                              onClick={() => { navigate('/gestao/vendedor'); setShowNotificacoes(false) }}
                              className="w-full flex items-start gap-3 px-4 py-3 hover:bg-green-50 transition-colors text-left"
                            >
                              <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mt-0.5">
                                <Send className="w-4 h-4 text-green-600" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-800">
                                  {notificacoes.repasses_pendentes} repasse{notificacoes.repasses_pendentes > 1 ? 's' : ''} pendente{notificacoes.repasses_pendentes > 1 ? 's' : ''}
                                </p>
                                <p className="text-xs text-green-600 font-semibold">
                                  {Number(notificacoes.valor_repasses_pendentes || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                </p>
                              </div>
                              <ChevronRight className="w-4 h-4 text-gray-400 mt-1 flex-shrink-0" />
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                    {notificacoes && notificacoes.total_vencidas > 0 && (
                      <div className="px-4 py-2.5 border-t border-gray-100 bg-gray-50">
                        <button
                          onClick={marcarTodasComoLidas}
                          className="w-full text-center text-xs text-blue-600 hover:text-blue-800 font-semibold py-1"
                        >
                          ✓ Marcar todas como lidas
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center gap-1.5 p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <UserAvatar name={user?.name} size="sm" />
                <ChevronDown className={`w-3.5 h-3.5 text-gray-400 hidden sm:block transition-transform duration-200 ${showUserMenu ? 'rotate-180' : ''}`} />
              </button>

              {showUserMenu && (
                <div className="absolute top-12 right-3 w-52 bg-white rounded-xl shadow-xl border border-gray-200 z-50 overflow-hidden">
                  <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
                    <p className="text-xs font-semibold text-gray-900 truncate">{user?.name}</p>
                    <p className="text-[10px] text-gray-500 truncate">{user?.email}</p>
                    <span className="inline-flex items-center mt-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-50 text-blue-600 capitalize">
                      {user?.permission_level?.replace('_', ' ')}
                    </span>
                  </div>
                  <div className="py-1">
                    <button
                      onClick={() => { logout(); setShowUserMenu(false) }}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <LogOut className="w-4 h-4" />
                      <span>Sair</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </header>

          {/* Banner de impersonation (super_admin visualizando org específica) */}
          {user?.permission_level === 'super_admin' && organizadorAtivo && (
            <div className="no-print flex-shrink-0 bg-amber-500 px-4 py-2.5 flex items-center justify-between gap-4 z-20">
              <div className="flex items-center gap-2.5 min-w-0">
                <AlertTriangle className="w-4 h-4 text-amber-900 flex-shrink-0" />
                <div className="min-w-0">
                  <span className="text-xs font-bold text-amber-900">Modo Impersonation Ativo</span>
                  <span className="text-xs text-amber-800 ml-2 hidden sm:inline">
                    Visualizando como: <strong>{organizadorAtivo.name}</strong> · Admin: {user.email}
                  </span>
                  <p className="text-xs text-amber-800 sm:hidden truncate">
                    {organizadorAtivo.name}
                  </p>
                </div>
              </div>
              <button
                onClick={() => { clearOrganizador() }}
                className="flex items-center gap-1.5 text-xs font-semibold bg-amber-900/20 hover:bg-amber-900/30 text-amber-900 px-3 py-1.5 rounded-lg transition-colors whitespace-nowrap flex-shrink-0"
              >
                <ArrowLeftFromLine className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Voltar para Visão Global</span>
                <span className="sm:hidden">Sair</span>
              </button>
            </div>
          )}

          {/* Banner de modo somente visualização */}
          {organizadorAtivo?.somente_visualizacao && (
            <div className="no-print flex-shrink-0 bg-orange-50 border-b border-orange-200 px-4 py-2 flex items-center gap-2 z-20">
              <EyeOff className="w-4 h-4 text-orange-600 flex-shrink-0" />
              <span className="text-sm text-orange-800">
                <strong>Modo somente visualização</strong> — Criação e edição de dados estão desabilitadas neste organizador.
              </span>
            </div>
          )}

          {/* Page content */}
          <main className="flex-1 overflow-y-auto p-3 md:p-4">
            {children}
          </main>
        </div>
      </div>
    </SidebarContext.Provider>
  )
}

export default function Layout({ children }) {
  return <LayoutContent>{children}</LayoutContent>
}
